import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, TrendingUp, Package, Building2 } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article7 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-green-600 text-white mr-4">
              <TrendingUp className="h-3 w-3 mr-1" />
              E-commerce
            </Badge>
            <span className="text-sm text-gray-600">EcommerceNews.eu</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>4 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.7.title')}
          </h1>
          
          <img 
            src="https://ecommercenews.eu/static/wp-content/uploads/2023/08/MediaMarkt-marketplace.jpg?w=360&h=240&bg=white&fm=webp" 
            alt="MediaMarkt Marketplace"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.7.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.7.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.7.p1')}
            </p>

            <p className="mb-6">
              {t('article.7.p2')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg my-8 border border-green-100">
              <h3 className="font-bold text-green-900 mb-2 flex items-center">
                <TrendingUp className="mr-2" />
                {t('article.7.highlight.title')}
              </h3>
              <p className="text-sm text-green-800">
                {t('article.7.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.7.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.7.p3')}
            </p>

            <p className="mb-6">
              {t('article.7.p4')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-blue-900 mb-4">{language === 'ko' ? 'Ceconomy 디지털 성과' : 'Ceconomy Digital Performance'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '온라인 매출 성장' : 'Online Revenue Growth'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '온라인 비율:' : 'Online Share:'}</strong> {language === 'ko' ? '30.1% 달성' : '30.1% achieved'}</li>
                    <li><strong>{language === 'ko' ? '디지털 성장:' : 'Digital Growth:'}</strong> {language === 'ko' ? '7% 증가' : '7% increase'}</li>
                    <li><strong>{language === 'ko' ? '연속 성장:' : 'Consecutive Growth:'}</strong> {language === 'ko' ? '12분기 연속' : '12 quarters straight'}</li>
                    <li><strong>{language === 'ko' ? '코비드 수준:' : 'Covid Level:'}</strong> {language === 'ko' ? '코비드 이후 최고' : 'Highest since Covid'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Package className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '마켓플레이스 확장' : 'Marketplace Expansion'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? 'GMV 목표:' : 'GMV Target:'}</strong> {language === 'ko' ? '€7.5억 달성 예상' : '€750m on track'}</li>
                    <li><strong>{language === 'ko' ? '전년 대비:' : 'YoY Growth:'}</strong> {language === 'ko' ? '90% 증가' : '90% increase'}</li>
                    <li><strong>{language === 'ko' ? '확장 계획:' : 'Expansion:'}</strong> {language === 'ko' ? '스위스, 헝가리 진출' : 'Switzerland, Hungary entry'}</li>
                    <li><strong>{language === 'ko' ? '총 국가:' : 'Total Countries:'}</strong> {language === 'ko' ? '10개국 예정' : '10 countries planned'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.7.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.7.p5')}
            </p>

            <p className="mb-6">
              {t('article.7.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.7.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.7.p7')}
            </p>

            <p className="mb-6">
              {t('article.7.p8')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-green-900 mb-4">{language === 'ko' ? '리테일 미디어 성과' : 'Retail Media Success'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-green-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '리테일 미디어 성장' : 'Retail Media Growth'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '수익 성장:' : 'Income Growth:'}</strong> {language === 'ko' ? '85% 이상 증가' : '85%+ increase'}</li>
                    <li><strong>{language === 'ko' ? '연간 목표:' : 'Annual Target:'}</strong> {language === 'ko' ? '€4,500만 달성 예상' : '€45m on track'}</li>
                    <li><strong>{language === 'ko' ? '광고 플랫폼:' : 'Ad Platform:'}</strong> {language === 'ko' ? '브랜드 파트너 타겟 광고' : 'Targeted ads for brand partners'}</li>
                    <li><strong>{language === 'ko' ? '데이터 활용:' : 'Data Utilization:'}</strong> {language === 'ko' ? '고객 데이터와 디지털 트래픽' : 'Customer data and digital traffic'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Package className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'JoyExpress 유럽 진출' : 'JoyExpress European Entry'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? 'JD.com 인수:' : 'JD.com Acquisition:'}</strong> {language === 'ko' ? 'Ceconomy 공식 인수 예정' : 'Official Ceconomy acquisition planned'}</li>
                    <li><strong>{language === 'ko' ? '서비스 지역:' : 'Service Areas:'}</strong> {language === 'ko' ? '영국, 독일, 네덜란드, 프랑스' : 'UK, Germany, Netherlands, France'}</li>
                    <li><strong>{language === 'ko' ? '배송 옵션:' : 'Delivery Options:'}</strong> {language === 'ko' ? '당일/익일 배송' : 'Same-day/next-day delivery'}</li>
                    <li><strong>{language === 'ko' ? '인프라:' : 'Infrastructure:'}</strong> {language === 'ko' ? '60개 이상 창고 및 데포' : '60+ warehouses and depots'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} EcommerceNews.eu - Ceconomy Passes 30% Online Revenue Share Milestone.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://ecommercenews.eu/ceconomy-passes-30-online-revenue-share/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article7;